
/**
 * Utility functions for image processing and optimization
 */

/**
 * Optimizes an image by resizing and compressing it
 * @param imageUrl - The source image URL (data URL or remote URL)
 * @param maxWidth - Maximum width in pixels
 * @param quality - JPEG quality (0-1)
 * @returns A promise that resolves to the optimized image data URL
 */
export const optimizeImage = (
  imageUrl: string, 
  maxWidth: number = 800, 
  quality: number = 0.7
): Promise<string> => {
  return new Promise((resolve) => {
    // If it's not a data URL, just return the original
    if (!imageUrl.startsWith('data:image')) {
      resolve(imageUrl);
      return;
    }
    
    const img = document.createElement('img');
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      // Resize if larger than max width
      if (width > maxWidth) {
        const ratio = height / width;
        width = maxWidth;
        height = Math.round(width * ratio);
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        // Convert to JPEG with reduced quality
        resolve(canvas.toDataURL('image/jpeg', quality));
      } else {
        // If we couldn't get a 2D context, return the original
        resolve(imageUrl);
      }
    };
    
    img.onerror = () => {
      console.error('Error loading image for optimization');
      resolve(imageUrl);
    };
    
    img.src = imageUrl;
  });
};

/**
 * Estimates the size of a data URL in bytes
 * @param dataUrl - The data URL to measure
 * @returns The approximate size in bytes
 */
export const estimateDataUrlSize = (dataUrl: string): number => {
  if (!dataUrl) return 0;
  if (!dataUrl.startsWith('data:')) return 0;
  
  // Get the base64 part after the comma
  const base64Part = dataUrl.split(',')[1];
  if (!base64Part) return 0;
  
  // Base64 encodes 3 bytes into 4 characters, so the size is roughly 3/4
  return Math.ceil(base64Part.length * 0.75);
};

/**
 * Formats bytes into a human-readable string
 * @param bytes - The number of bytes
 * @returns A formatted string (e.g., "1.5 MB")
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
